import { type MouseEvent, useState } from "react";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, Link, Typography } from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";

import { IncludedWithPlanInfo } from "../../../../Components/IncludedWithPlanInfo/IncludedWithPlanInfo";
import HtmlTooltip from "../../../../Components/Tooltip/HtmlTooltip";

type TooltipLink = {
  href: string;
  title: string;
};

type Props = {
  title: string;
  tooltipContentTitle: string;
  tooltipContentLink?: TooltipLink;
  isEntitled: boolean;
  children?: React.ReactNode;
};

const TooltipText: React.FC<Props> = ({ title, tooltipContentTitle, tooltipContentLink, isEntitled, children }) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const handleTooltipOpen = (e: MouseEvent) => {
    e.preventDefault();
    setIsTooltipOpen(true);
  };

  const handleTooltipClose = () => {
    setIsTooltipOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <Box component="span">
        <HtmlTooltip
          onClose={handleTooltipClose}
          open={isTooltipOpen}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          slotProps={{
            tooltip: {
              sx: { maxWidth: "340px" },
            },
          }}
          title={
            // Tooltip content
            <Box sx={{ width: "300px" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
                {tooltipContentTitle}
              </Typography>
              <IncludedWithPlanInfo isIncluded={isEntitled} />
              {children && <>{children}</>}
              {tooltipContentLink && (
                <Link
                  color="primary"
                  sx={{ textDecoration: "none" }}
                  href={tooltipContentLink.href}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Typography variant="body2" sx={{ display: "flex", alignItems: "center", pt: 1.4 }}>
                    {tooltipContentLink.title}
                    <OpenInNewIcon sx={{ ml: 0.5, fontSize: "14px" }} />
                  </Typography>
                </Link>
              )}
            </Box>
          }
        >
          <Box component="span" sx={{ borderBottom: "1px dotted ", cursor: "pointer" }} onClick={handleTooltipOpen}>
            {title}
          </Box>
        </HtmlTooltip>
      </Box>
    </ClickAwayListener>
  );
};

export default TooltipText;
